/**
 * 表格功能
 *   * 排序(多列排序, 默认带有弹窗交互) (报表编辑不可以)
 *   * 前端分页
 *   * 分页默认每页50条, ...
 *   * 拖拽调整列宽 (可控的, 图表可以, 报表不可以)
 *   * 表头弹窗展示描述 (表头还有排序弹窗)
 *   * 底部数据增加统计值
 *   * 单元格展示url
 *
 *
 */

// source 原始数据
// sortable 表格是否可以排序(报表编辑页面不可以)
// 暂时不要: defaultSorts 默认排序, 没有排序空数组
// sorts 外界控制排序 (但是又想表格内部排序 不会反复触发渲染, 但要传递给外界进行保存)(可以通过ref传递给外界改变排序的方法)

// pageConfig  分页配置
// defaultPageSize 默认每页条数, 默认是50
// statisticsData 底部统计值 (可以考虑外界传入pipeline的处理方法)
// config 表格展示配置 (图表配置)(可以考虑外界传入pipeline的处理方法)
//    config.
// 用户定义的默认  列宽
// 拖拽列宽功能(可以考虑外界传入pipeline的处理方法)

/**
 * 生成基础的columns
 * source 数据 (计算加载完成后才有)
 * 表格的默认配置 (初始化时, 早于source加载完成),
 *   排序:  图表信息加载完成后, 外部控制(pipeline.use(features.sort({...原本的配置, sorts: [新配置]})); 外部可以获取状态 pipeline.getState()
 *   列宽:  图表信息加载完成后(页面初始化时, 设置一次), 用户拖拽修改就不进行外部控制, 但每次变更外部可以获取状态 pipeline.getState()
 *
 *   表头描述: 图表信息加载完成后, 外部控制
 *   单元格渲染: 普通, url, 图表信息加载完成后, 外部控制 (可以通过pipeline.columns([新的配置]))
 *
 *
 * config:
 *  sortable 控制是否可以排序
 * */

import { getLocale } from "umi";
import { getTableCompareText } from "@/pages/datalab/utils/utils";
import { formatDisplayValue } from "@customComp/Chart/_config/_utils";
import { FieldType } from '@dbl/core/dataSource';
import { LANGUAGE_EN } from "@home/_config/config";
import { STATISTICS_LIST, STATISTICS_PREFIX } from "./consts";
import { AliTableCellTypeEnum, ColumnDataType, SortFunction, TAliTableColumn, TSort, ITableColumnInfo } from "@customComp/AliTableAnalytics/types";
import {
  applyConditionalFormatToColumns,
  type TConditionalFormattingInput,
} from '@customComp/AliTableAnalytics/utils/applyColumnConditionalFormat';
import { isNumericString } from '@dbl/core/dataSource';
import { getLanguage } from "@home/utils/common";
import { isEmpty } from "lodash-es";
// ==================== 前缀数字自然排序（自然排序） ====================
/**
 * 提取字符串前缀数字（支持小数点和千分位逗号）
 * "123abc" → [true, "123", "abc"]
 * "-10test" → [true, "-10", "test"]
 * "3.14abc" → [true, "3.14", "abc"]
 * "1,234.56abc" → [true, "1,234.56", "abc"]
 * "1,234,567abc" → [true, "1,234,567", "abc"]
 * "3.14.5abc" → [true, "3.14", ".5abc"]  (小数点只匹配一次)
 * "abc123" → [false, "", "abc123"]
 */
const normalizeSortString = (value: unknown): string => {
  if (value == null) return '';
  return String(value);
};

const extractPrefixNumber = (input: unknown): [boolean, string, string] => {
  const s = normalizeSortString(input);
  // 匹配: 可选负号 + 数字 + (逗号+数字)* + (小数点+数字)?
  const match = s.match(/^(-?[0-9]+(?:,[0-9]+)*(?:\.[0-9]+)?)(.*)/s);
  if (match) {
    return [true, match[1], match[2]];
  }
  return [false, '', s];
};

/** 判断字符串是否为有符号浮点数格式 */
const isSignedFloat = (s: string): boolean => /^-?[0-9]+\.[0-9]+$/.test(s);

/** 去除前导 0，保留至少一位（"0" → "0"，"007" → "7"） */
const stripLeadingZeros = (s: string): string => {
  const stripped = s.replace(/^0+/, '');
  return stripped || '0';
};

/** 无符号整数字符串比较（支持超大数字），先去除前导0再按长度优先 + 字典序比较，同值返回0保持原输入顺序 */
const compareUnsigned = (a: string, b: string): number => {
  const aStripped = stripLeadingZeros(a);
  const bStripped = stripLeadingZeros(b);
  if (aStripped.length !== bStripped.length) return aStripped.length - bStripped.length;
  if (aStripped < bStripped) return -1;
  if (aStripped > bStripped) return 1;
  return 0;
};

/** 有符号数字字符串比较（支持超大整数和小数，支持千分位逗号） */
const compareSignedNumbers = (a: string, b: string): number => {
  // 去除千分位逗号，还原为纯数字字符串
  const aClean = a.replace(/,/g, '');
  const bClean = b.replace(/,/g, '');
  const aNeg = aClean.startsWith('-');
  const bNeg = bClean.startsWith('-');
  if (aNeg !== bNeg) return aNeg ? -1 : 1;
  const aAbs = aNeg ? aClean.slice(1) : aClean;
  const bAbs = bNeg ? bClean.slice(1) : bClean;
  const isFloat = isSignedFloat(aClean) || isSignedFloat(bClean);
  if (isFloat) {
    const [aInt, aDec = ''] = aAbs.split('.');
    const [bInt, bDec = ''] = bAbs.split('.');
    const maxDecLen = Math.max(aDec.length, bDec.length);
    const aPadded = aInt.padStart(bInt.length, '0') + aDec.padEnd(maxDecLen, '0');
    const bPadded = bInt.padStart(aInt.length, '0') + bDec.padEnd(maxDecLen, '0');
    const result = compareUnsigned(aPadded, bPadded);
    return aNeg ? -result || 0 : result;
  }
  const result = compareUnsigned(aAbs, bAbs);
  return aNeg ? -result || 0 : result;
};

/**
 * 前缀数字自然排序比较器
 * 排序优先级：
 * 1. 有前缀数字 vs 无前缀数字 → 有前缀数字排前面
 * 2. 前缀数字值比较（支持超大数字和小数）
 * 3. 剩余字符串按 UTF8 码点顺序比较
 */
const prefixNumberCompare = (a: string, b: string): number => {
  if (a === b) return 0;
  const [aHasPrefix, aNum, aRest] = extractPrefixNumber(a);
  const [bHasPrefix, bNum, bRest] = extractPrefixNumber(b);
  if (aHasPrefix && bHasPrefix) {
    const numCmp = compareSignedNumbers(aNum, bNum);
    if (numCmp !== 0) return numCmp;
    // 纯字符串部分忽略大小写比较
    const aRestLower = aRest.toLowerCase();
    const bRestLower = bRest.toLowerCase();
    const restCmp = aRestLower < bRestLower ? -1 : aRestLower > bRestLower ? 1 : 0;
    if (restCmp !== 0) return restCmp;
    // 大小写不同但 lowercase 相同时，保留原始大小写顺序作为 tie-breaker
    return aRest < bRest ? -1 : aRest > bRest ? 1 : 0;
  }
  if (aHasPrefix !== bHasPrefix) return aHasPrefix ? -1 : 1;
  // 纯字符串忽略大小写比较
  const aLower = a.toLowerCase();
  const bLower = b.toLowerCase();
  if (aLower !== bLower) return aLower < bLower ? -1 : 1;
  // 大小写不同但 lowercase 相同时，保留原始大小写顺序作为 tie-breaker
  return a < b ? -1 : 1;
};

type TDateTimePattern = {
  regex: RegExp;
  handler: (y: number, m: number, d: number, h: number, min: number, s: number) => number;
};

const patterns: TDateTimePattern[] = [
  // 完整日期时间（含秒） %Y-%M-%D %H:%M:%S
  {
    regex: /^(\d{4})-(\d{1,2})-(\d{1,2}) (\d{1,2}):(\d{1,2}):(\d{1,2})$/,
    handler: (y, m, d, h, min, s) => {
      const date = new Date();
      date.setFullYear(y, m - 1, d);
      date.setHours(h, min, s);
      return date.getTime();
    }
  },
  // 完整日期时间（不含秒） %Y-%M-%D %H:%M
  {
    regex: /^(\d{4})-(\d{1,2})-(\d{1,2}) (\d{1,2}):(\d{1,2})$/,
    handler: (y, m, d, h, min) => {
      const date = new Date();
      date.setFullYear(y, m - 1, d);
      date.setHours(h, min, 0);
      return date.getTime();
    }
  },
  // 月日格式（不含秒） %M-%D %H:%M
  {
    regex: /^(\d{1,2})-(\d{1,2}) (\d{1,2}):(\d{1,2})$/,
    handler: (m, d, h, min) => {
      const date = new Date();
      date.setMonth(m - 1, d);
      date.setHours(h, min, 0);
      return date.getTime();
    }
  },
  // 美式日期 %M-%D-%Y
  {
    regex: /^(\d{1,2})-(\d{1,2})-(\d{4})$/,
    handler: (m, d, y) => {
      const date = new Date();
      date.setFullYear(y, m - 1, d);
      date.setHours(0, 0, 0);
      return date.getTime();
    }
  },
  // 标准日期 %Y-%M-%D
  {
    regex: /^(\d{4})-(\d{1,2})-(\d{1,2})$/,
    handler: (y, m, d) => {
      const date = new Date();
      date.setFullYear(y, m - 1, d);
      date.setHours(0, 0, 0);
      return date.getTime();
    }
  },
  // 年月格式 %Y-%M
  {
    regex: /^(\d{4})-(\d{1,2})$/,
    handler: (y, m) => {
      const date = new Date();
      date.setFullYear(y, m - 1, 1);
      date.setHours(0, 0, 0);
      return date.getTime();
    }
  },
  // 月日格式 %M-%D
  {
    regex: /^(\d{1,2})-(\d{1,2})$/,
    handler: (m, d) => {
      const date = new Date();
      date.setMonth(m - 1, d);
      date.setHours(0, 0, 0);
      return date.getTime();
    }
  },
  // 仅时间（含秒） %H:%M:%S
  {
    regex: /^(\d{1,2}):(\d{1,2}):(\d{1,2})$/,
    handler: (h, min, s) => {
      const date = new Date();
      date.setHours(h, min, s);
      return date.getTime();
    }
  },
  // 仅时间（不含秒） %H:%M
  {
    regex: /^(\d{1,2}):(\d{1,2})$/,
    handler: (h, min) => {
      const date = new Date();
      date.setHours(h, min, 0);
      return date.getTime();
    }
  },
  // 仅年份 %Y
  {
    regex: /^(\d{4})$/,
    handler: (y) => {
      const date = new Date();
      date.setFullYear(y, 0, 1);
      date.setHours(0, 0, 0);
      return date.getTime();
    }
  },
  // 仅月份 %M
  {
    regex: /^(\d{1,2})$/,
    handler: (m) => {
      const date = new Date();
      date.setMonth(m - 1, 1);
      date.setHours(0, 0, 0);
      return date.getTime();
    }
  },
  // 仅日期 %D（特殊处理：假设为当月日期）
  {
    regex: /^(\d{1,2})$/,
    handler: (d) => {
      const date = new Date();
      date.setDate(d);
      date.setHours(0, 0, 0);
      return date.getTime();
    }
  }
];

// 内部使用的解析函数（箭头函数）
const parseDateTime = (dateTimeStr: string): number => {
  if (!dateTimeStr) return 0;
  // 尝试匹配所有模式
  for (const pattern of patterns) {
    // 后端返回的表格数据中, 列定义是String类型, 实际返回的数据是Number类型, 所以String(dateTimeStr)
    const match = String(dateTimeStr).match(pattern.regex);
    if (match) {
      return pattern.handler(...match.slice(1)?.map(Number));
    }
  }
  // 无法识别的格式返回0
  return 0;
};

/**
 * 统一的“统计值排在最后”比较器包装
 * @param aSortObj 值
 * @param bSortObj 值
 * @param baseCompare 非统计值排序 
 * @returns number
 */
const compareStatisticsLast = (
  aSortObj: TSort,
  bSortObj: TSort,
  baseCompare: () => number,
): number => {
  const isAStatistics = aSortObj?.is_statistics === true;
  const isBStatistics = bSortObj?.is_statistics === true;
  // 统计值不参与排序，保持原始相对位置（通常在底部）
  if (isAStatistics || isBStatistics) return 0;
  return baseCompare();
};

/**
 * 日期排序函数
 * @param a 第一个日期值
 * @param b 第二个日期值
 * @returns 排序结果 (-1, 0, 1)
 */
export const dateSort: SortFunction = (aSortObj, bSortObj): number =>
  compareStatisticsLast(aSortObj, bSortObj, () => {
    const a = normalizeSortString(aSortObj?.value);
    const b = normalizeSortString(bSortObj?.value);
    const timestampA = parseDateTime(a);
    const timestampB = parseDateTime(b);
    return timestampA - timestampB;
  });

/**
 * 数字排序函数
 * @param a 第一个数字值
 * @param b 第二个数字值
 * @returns 排序结果 (-1, 0, 1)
 */
const numberSort: SortFunction = (aSortObj, bSortObj) =>
  compareStatisticsLast(aSortObj, bSortObj, () => {
    const a = normalizeSortString(aSortObj?.value);
    const b = normalizeSortString(bSortObj?.value);
    if (a === b) {
      return 0;
    }
    // 处理空字符串
    const aIsEmpty = a === '';
    const bIsEmpty = b === '';
    if (aIsEmpty || bIsEmpty) {
      return aIsEmpty ? -1 : 1;
    }
    // 检查数字类型
    // 使用 isNumericString 进行更严格的数字判断，排除 Infinity、-Infinity、NaN 以及包含字母的字符串
    const aIsNum = isNumericString(a);
    const bIsNum = isNumericString(b);
    // 处理非数字
    if (!aIsNum || !bIsNum) {
      if (aIsNum !== bIsNum) {
        return aIsNum ? 1 : -1; // 数字排非数字后面
      }
      return a < b ? -1 : 1; // 非数字 使用原始字符串比较，接近 UTF8 的码点顺序
      // return a?.localeCompare(b); // 使用 localeCompare 进行升序排序时, 男会排在女前面, 而后端数据库使用UTF8 的码点排序, 女会排在男前面, 造成前后端不一致.
    }
    // 两个都是数字，解析后比较
    const aNum = parseFloat(a);
    const bNum = parseFloat(b);
    return aNum - bNum;
  });

/**
 * 百分比排序函数
 * @param a 第一个百分比值 (如 "50%")
 * @param b 第二个百分比值 (如 "30%")
 * @returns 排序结果 (-1, 0, 1)
 */
const percentSort: SortFunction = (aSortObj, bSortObj) =>
  numberSort({ ...(aSortObj || {}), value: aSortObj?.value?.replace('%', '') }, { ...(bSortObj || {}), value: bSortObj?.value?.replace('%', '') });

/**
 * 字符串类型排序函数
 *  表格数据中 字段类型是 string时, 展示出来的可能是一个 日期时间格式, 也可能是一个数字, 这种时候就需要按照日期或数字格式排序, 就不能用单纯的字符排序了
 *  支持前缀数字自然排序：如 "1_android", "10_ios", "2_web" 会按 1, 2, 10 排序
 * @param a 第一个数字值
 * @param b 第二个数字值
 * @returns 排序结果 (-1, 0, 1)
 */
const stringSort: SortFunction = (aSortObj, bSortObj) =>
  compareStatisticsLast(aSortObj, bSortObj, () => {
    const a = normalizeSortString(aSortObj?.value);
    const b = normalizeSortString(bSortObj?.value);
    // 是字符串类型, 但字符串是日期时间
    const timestampA = parseDateTime(a);
    const timestampB = parseDateTime(b);
    if (timestampA !== 0 && timestampB !== 0) {
      const result = timestampA - timestampB;
      if (result !== 0) return result;
    } else {
      const result = prefixNumberCompare(a, b);
      if (result !== 0) return result;
    }
    // 值相等时返回 0，让 ali-react-table 多列排序可以继续检查下一个排序列
    // 排序稳定性由 Array.prototype.sort() (ES2019+) 保证
    return 0;
  });

// 排序方法映射表
// 所有排序方法都按照 升序规则返回, ali表格内部会自己转换升序降序
const SortMap: Record<ColumnDataType, SortFunction | boolean> = {
  date: dateSort,
  // string: true, // 使用默认的字符串排序
  string: stringSort, // 默认字典序的排序, 数字无法从大到小的排序
  number: numberSort,
  float: numberSort,
  percent: percentSort,
};

/**
 * 生成表格列配置
 * @param columnInfos 列信息数组
 * @param config 配置对象 {isEn: 是否英文, sortable: 是否可排序}
 * @returns TAliTableColumn[] 表格列配置数组
 */
export const generateTableColumns = (columnInfos: ITableColumnInfo[], config: { isEn: boolean; sortable: boolean; freezeCount: number; }): TAliTableColumn[] => {
  // columnInfos 表格列的信息数据数组
  // sortable 控制是否可以排序
  const { isEn, sortable, freezeCount } = config;
  const columns: TAliTableColumn[] = [];
  (columnInfos || []).forEach((column: ITableColumnInfo, index: number) => {
    const { code, name, name_cn: nameCn, name_en: nameEn, table_code: tableCode, origin_name: originName, type, column_date_type: dateType, accuracy, unit, is_compare_date: isCompareDate, is_user_filter: isUserFilter } = column;
    // 优先使用 name_cn 和 name_en 进行中英文翻译
    let title: string;
    if (nameCn || nameEn) {
      // 如果提供了中英文标题，直接使用
      title = getLanguage(isEn, nameEn, nameCn) ?? name;
    } else {
      // 为了兼容t-1这种数据, 所以这里标题截取时, 以后端返回的table_code为标准, 不再使用固定的t0, t1, t2
      // 不区分中英文展示, 对比项文本需要前端做翻译
      title = getTableCompareText(isEn, name, isCompareDate, isUserFilter)?.replace(`${tableCode || 't0'}_`, '');
    }
    columns.push({
      code: name || code,
      name: title,
      type,
      isEn,
      isCompareDate,
      isUserFilter,
      // title, title 可以覆盖name
      width: 180,
      align: (dateType === 'string' || dateType === 'date') ? 'left' : 'right',
      // 设置 sortable: true时, 不显示排序按钮的逻辑会在MultipleSortHeaderCell组件中处理.
      features: sortable ? { sortable: SortMap[dateType as ColumnDataType] } : { sortable: true },
      lock: index < freezeCount,
      // 直接传递自己所需的数据, 不是ali-react-table原本的属性
      valueType: dateType,
      cellType: AliTableCellTypeEnum.Original, // 代表单元格要以哪种形式渲染, 目前是 普通, url
      // 获取表头描述的key
      descKey: `${tableCode || 't0'}_${originName}_${type}`,
      // !!!所有配置列的对应都是tableCode+originName作为唯一值
      originName,
      tableCode: tableCode || 't0',
      // 数据展示的小数位数
      accuracy: accuracy || 0,
      // 用bmk还是千分隔符展示
      unit: unit || '',
    });
  });
  return columns;
};

/**
 * 设置列宽
 * @param columns 列配置数组
 * @param widths 宽度数组
 * @returns 更新宽度后的列配置数组
 */
export const setColumnWidth = (columns: TAliTableColumn[], widths: number[]): TAliTableColumn[] =>
  columns.map((column, index) => ({ ...column, width: widths[index] || 180 }));

export const setColumnConditionalFormatting = (
  columns: TAliTableColumn[],
  conditionalFormatting: unknown,
): TAliTableColumn[] =>
  applyConditionalFormatToColumns(
    columns,
    conditionalFormatting as TConditionalFormattingInput,
  );

export const getCellText = (value: string, column: { valueType: ColumnDataType; accuracy: number; unit: string; }): string => {
  const { valueType, accuracy, unit } = column;
  // 表格中 空字符串和只有空格的字符串, 在单元格中都是正常展示, 不展示 -, 所以单元格看起来没有任何内容
  let text = '';
  if (value === 'null') {
    // null 字符串 展示为null字符串
    text = 'null';
  } else if (valueType === FieldType.String || valueType === FieldType.BigInt || valueType === FieldType.Decimal) {
    // 展示后端返回原始值的数据类型，不做数值格式化
    // bigint和decimal类型展示为原始值，不做数值格式化(ab实验行数据的valueType格式化引入)
    text = value === 'null' ? '-' : value;
  } else if ((typeof value === 'number') && !isNaN(value)) {
    text = value;
  } else if ((typeof value === 'string') && value?.trim()) {
    // null字符串会返回 '-'
    text = formatDisplayValue({
      value,
      isPercent: valueType === FieldType.Percent,
      accuracy,
      unit,
    });
  }
  return text;
};

// 排序气泡文本映射
const orderTextMap = {
  desc: {
    en: 'Descend',
    cn: '降序',
  },
  asc: {
    en: 'Ascend',
    cn: '升序'
  }
};

/**
 * 获取排序气泡文本
 * @param param0 参数对象 {sortNumber: 排序序号, order: 排序方式}
 * @returns 本地化排序提示文本
 */
export const getSortBubbleText = ({ sortNumber, order, sortCount }: { sortNumber: number; order: 'asc' | 'desc'; sortCount: number; }): string => {
  const isEn = getLocale() === LANGUAGE_EN;
  if (isEn) {
    return `Sort ${sortNumber}: ${orderTextMap[order]?.en}`;
  }
  const temp = sortCount > 1 ? `第${sortNumber}个排序` : '排序';
  return `${temp}：${orderTextMap[order]?.cn}`;
};

/**
 * 获取分页数据
 * @param data 原始数据数组
 * @param pageSize 每页条数
 * @param currentPage 当前页码(从1开始)
 * @returns 当前页的数据
 */
export const getPaginatedData = (data: any[], pageSize?: number, currentPage?: number) => {
  // 参数验证
  if (!Array.isArray(data)) {
    console.error('第一个参数必须是数组');
    return [];
  }

  if (typeof pageSize !== 'number' || pageSize <= 0) {
    console.error('pageSize必须是大于0的数字');
    return [];
  }

  if (typeof currentPage !== 'number' || currentPage < 1) {
    console.error('currentPage必须是大于等于1的数字');
    return [];
  }

  // 计算起始和结束索引
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  // 返回当前页的数据
  return data.slice(startIndex, endIndex);
};

/**
 * 判断是否是统计值关键词
 * @param value 要检查的值
 * @returns 是否是统计值关键词
 */
export const isStatisticsKeyword = (value: string): boolean =>
  STATISTICS_LIST?.includes(value?.replace?.(STATISTICS_PREFIX, ''));

/**
 * 虚拟滚动, 表格自动开启, 可以不配置
 * useVirtual: {
 *             horizontal: isOpenHorizontal,
 *             vertical: isOpenVertical,
 *             header: isOpenVertical && isOpenHorizontal,
 *           },
 *
 * */